import React, { useState } from 'react';

import {
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography,
    Switch,
    CircularProgress,
    Alert,
    Box,
} from '@mui/material';
import { Button } from '@apigrowfinance/pbolt-button';
import { PDialogAction, PDialogContent } from '@apigrowfinance/pbolt-dialog';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import {
    AccountStatus,
    IPatchUpdateAccountStatusDetails,
} from '@clients/accountsClient';
import accountStatusTranslations from '@messages/accountStatus';
import AccountStatusFormReason from '@pages/accountContainer/AccountStatus/AccountStatusFormReason';
import getEnumTranslation from '@utils/getEnumTranslation';

interface AccountStatusFormProps {
    accountStatuses: AccountStatus[];
    activeStatus: string;
    closeModal: () => void;
    isLoadingStatuses: boolean;
    readyToSubmitData: (data: IPatchUpdateAccountStatusDetails) => void;
    error?: AxiosError | null;
}

const AccountStatusForm = ({
    isLoadingStatuses,
    accountStatuses,
    activeStatus,
    closeModal,
    error,
    readyToSubmitData,
}: AccountStatusFormProps) => {
    const { handleSubmit, control } = useForm();
    const { theme } = useTheme();

    const [reasonCanBeGiven, setReasonCanBeGiven] = useState(false);
    const [statusHasChanged, setStatusHasChanged] = useState(false);
    const reasonToggleChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        value: boolean,
    ) => {
        setReasonCanBeGiven(value);
    };
    const onSubmit = (data: IPatchUpdateAccountStatusDetails) => {
        const dataToSubmit = Object.assign({}, data);
        // If the user has untoggled 'give reason' but already filled
        // out the fields, we don't want to send those fields
        if (!reasonCanBeGiven) {
            delete dataToSubmit.description;
            delete dataToSubmit.reason_id;
        }
        readyToSubmitData(dataToSubmit);
    };

    const handleRadioGroupChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        value: string,
    ) => {
        setStatusHasChanged(value !== activeStatus);
    };
    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <PDialogContent>
                <Controller
                    name="status"
                    control={control}
                    render={({ field: { onChange } }) => (
                        <RadioGroup
                            name="status-radio-group"
                            onChange={(event, value) => {
                                onChange(value);
                                handleRadioGroupChange(event, value);
                            }}
                            defaultValue={activeStatus}>
                            {isLoadingStatuses && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}>
                                    <CircularProgress data-testid="statuses-loading" />
                                </Box>
                            )}
                            <Grid container spacing={2} mb={5.625} mt={1.375}>
                                {accountStatuses?.map(({ name }) => {
                                    return (
                                        <Grid
                                            item
                                            xs={12}
                                            sm={4}
                                            key={`radio-status-${name?.toLowerCase()}`}>
                                            <FormControlLabel
                                                value={name}
                                                label={
                                                    <Typography variant="subtitle2">
                                                        {getEnumTranslation(
                                                            name,
                                                            {
                                                                messages:
                                                                    accountStatusTranslations,
                                                            },
                                                        )}
                                                    </Typography>
                                                }
                                                control={<Radio size="small" />}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </RadioGroup>
                    )}
                />
            </PDialogContent>
            {statusHasChanged && (
                <PDialogContent>
                    <Grid container>
                        <Grid item xs={10}>
                            <Typography
                                color={
                                    theme?.custom?.brand?.textMain ||
                                    'text.primary'
                                }
                                variant="h6"
                                component="h3">
                                <FormattedMessage
                                    id="AccountStatusForm.incReasonChange"
                                    defaultMessage="Include reason for change"
                                />
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={2}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}>
                            <Switch
                                data-testid="toggle-reason"
                                onChange={reasonToggleChange}
                                size="small"
                            />
                        </Grid>
                    </Grid>
                    <Typography
                        id="modal-modal-description"
                        fontSize={`${0.75}rem`}>
                        <FormattedMessage
                            id="AccountStatus.incReasonChangeDesc"
                            defaultMessage="This will help colleagues to understand why the status has been changed."
                        />
                    </Typography>
                    {reasonCanBeGiven && (
                        <AccountStatusFormReason control={control} />
                    )}
                </PDialogContent>
            )}
            {error && (
                <Alert severity="error">{error?.response?.data?.message}</Alert>
            )}
            <PDialogAction>
                <Button onClick={closeModal}>
                    {getEnumTranslation('cancel')}
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    disabled={!statusHasChanged}>
                    {getEnumTranslation('done')}
                </Button>
            </PDialogAction>
        </form>
    );
};
export default AccountStatusForm;
